/**
 * retrieval.mjs — search + paste + ask, with the search actually happening.
 *
 * FOUNDER, 2026-08-14: "ai101 labs next" → chapter 5.
 *
 * ── WHY THIS LAB CAN BE HONEST AT ALL ─────────────────────────────────────────────────────────
 * §2: "The hard part is not the AI; it is the retrieval. Bad retrieval means the model gets the
 * wrong chunks and produces a confident wrong answer."
 *
 * That sentence is what makes chapter 5 buildable without a model. Retrieval is arithmetic over
 * documents — it can be done here, for real, on a real corpus, with every score shown. The ANSWER
 * cannot, and the lab does not fake one. It shows the chunks that were retrieved and the prompt
 * they were stuffed into, and stops. Which is not a limitation dressed up: his own claim is that
 * the answer follows from the chunks, so showing the chunks IS showing what determines the answer.
 *
 * ── SIX TIMES TODAY I SHORTENED A QUOTATION ───────────────────────────────────────────────────
 * Three of these quotes were caught by the gate as elisions — his parentheticals and a following
 * sentence, trimmed to fit a UI slot. It is the same habit every time and the gates are what stop
 * it: they match against the module, never against my memory of it. Full sentences here, including
 * the bracketed examples that make them concrete.
 *
 * ── THE CORPUS IS THE COURSE ──────────────────────────────────────────────────────────────────
 * The seven CodeAI101 modules, already compiled into a shelf for the reading surface. Real
 * documents, already on disk, already cited. Nothing invented to search over.
 *
 * ── TWO REAL RETRIEVERS, AND THE HONEST RESULT IS NOT THE DRAMATIC ONE ────────────────────────
 * The lab ships two and lets the learner switch: naive word overlap, and the standard fix of
 * weighting a word by how rare it is. The naive one is not a strawman — it is what anyone writes
 * first — and crippling it until it returned nonsense would have made a better demonstration and a
 * worse lesson.
 *
 * Measured before this was written: across eight questions the two picked a different top three on
 * FIVE, and the difference was always the second or third hit, never the first. On sixty-three
 * chunks that are all about AI, common words genuinely carry information and the naive scorer is
 * mostly right. So the lab reports what the two actually did on the learner's own question, and
 * names the situation it is in — a distinctiveness weighting earns its keep as a corpus grows and
 * diversifies, which is the same finding as the Code101 shelf. On one course it is a refinement; on
 * a company's whole knowledge base it is the difference between the right document and a confident
 * wrong answer.
 */

export const SOURCE_1 = 'CodeAI101 module 05 §1 "Why RAG exists — the stale-and-generic problem"';
export const SOURCE_2 = 'CodeAI101 module 05 §2 "The shape of RAG — retrieve, stuff, answer"';
export const SOURCE_7 = 'CodeAI101 module 05 §7 "RAG vs fine-tuning vs longer prompt — when to use which"';

export const NOT_EXOTIC = 'RAG is not exotic. It is "search + paste + ask" — wrapped in a system that does the search and the pasting automatically every time someone asks a question.';
export const HARD_PART = 'The hard part is not the AI; it is the retrieval. Bad retrieval means the model gets the wrong chunks and produces a confident wrong answer. Good retrieval means the model gets exactly the right chunks and produces a useful right answer.';
export const THE_FIX = 'RAG is the better solution: leave the model as-is, and at the moment of the question, fetch the relevant fresh-and-specific documents and put them into the prompt.';

/** §1's two problems, quoted. */
export const PROBLEMS = Object.freeze([
  { id: 'stale', name: 'It is stale',
    quote: 'The model was trained on data up to some date — the training cutoff — and knows nothing after that. By the time you use it, months or years may have passed. New laws have been written, new products released, new prices set, new versions shipped. The model speaks as if those changes never happened.' },
  { id: 'generic', name: 'It is generic',
    quote: 'The model was trained on the internet. It knows the average of what was written about any topic. It does not know your company\'s specific policy, your project\'s specific data, your organisation\'s specific contract.' },
]);

/** §2's three steps, quoted, in his order. */
export const STEPS = Object.freeze([
  { n: 1, name: 'Retrieve',
    quote: 'Given the user\'s question, search a collection of documents (the company\'s knowledge base, the patient\'s record, the codebase, the manuals) and pull out the few that are most relevant. Usually three to ten chunks of a few paragraphs each.' },
  { n: 2, name: 'Stuff',
    quote: 'Take those relevant chunks and put them into the prompt, along with the user\'s question.' },
  { n: 3, name: 'Answer',
    quote: 'The model reads the prompt — chunks plus question — and generates an answer based on what was in the chunks. Ideally, the model cites which chunks it drew from, so the user (or an auditor) can verify.' },
]);

/** His prompt template, verbatim — the shape of what gets stuffed. */
export const TEMPLATE = 'Here are some relevant excerpts: [chunks]. Now, given those, answer this question: [question].';

/** §7's three options and his rule of thumb, quoted. */
export const OPTIONS = Object.freeze([
  { id: 'prompt', name: 'Longer prompt', quote: 'paste the relevant context directly into the conversation. Works when the context is small and the conversation is brief.' },
  { id: 'rag', name: 'RAG', quote: 'store all the context in a vector database; retrieve and stuff what is relevant at the moment of each question. Works when the context is large or you have many users / questions.' },
  { id: 'finetune', name: 'Fine-tuning (LoRA)', quote: 'adjust the model itself by training it on your data. Works when the style or behaviour you want the model to have cannot be captured by retrieval (e.g., "respond in our company\'s specific tone of voice").' },
]);
export const RULE_OF_THUMB = 'prefer prompt (simplest), use RAG when prompt would not fit or would need to vary by question (most common in production), use fine-tuning only when retrieval cannot capture the behaviour change you need (rare for most teams).';

const STOP = new Set(['the', 'a', 'an', 'of', 'to', 'and', 'is', 'it', 'in', 'that', 'this', 'for',
  'as', 'on', 'at', 'by', 'or', 'be', 'are', 'was', 'you', 'your', 'not', 'but', 'with', 'what',
  'how', 'does', 'do', 'can', 'will', 'they', 'them', 'its', 'has', 'have']);
const tok = (s) => String(s || '').toLowerCase().match(/[a-z][a-z-]{1,}/g) || [];

/**
 * TWO RETRIEVERS, AND THE BAD ONE IS NOT A STRAWMAN.
 *
 * `overlap` is the naive thing a person actually writes first: count how many query words appear.
 * Common words dominate, so the chunks that win are the ones that are longest and most ordinary.
 * `distinctive` weights a word by how RARE it is across the corpus, which is the standard fix.
 *
 * Both are real, both are shown with their scores, and the learner switches between them on the
 * same question. That is §2's sentence — the model is identical, the chunks are not.
 */
export const RETRIEVERS = Object.freeze({
  overlap: { id: 'overlap', name: 'Naive — count matching words',
    why: 'The first thing anyone writes. Every word counts the same, so common words decide, and the longest, most ordinary paragraphs win.' },
  distinctive: { id: 'distinctive', name: 'Weighted — rare words count more',
    why: 'A word that appears in every chunk tells you nothing. Weighting by rarity is the standard fix, and it is most of what "good retrieval" means in practice.' },
});

export function buildIndex(chunks) {
  const docs = chunks.map((c) => ({ ...c, terms: tok(c.body) }));
  const df = new Map();
  for (const d of docs) for (const w of new Set(d.terms)) df.set(w, (df.get(w) || 0) + 1);
  return { docs, df, N: docs.length };
}

export function retrieve(index, question, { how = 'distinctive', k = 3 } = {}) {
  const q = [...new Set(tok(question))].filter((w) => !STOP.has(w));
  const scored = index.docs.map((d) => {
    const counts = new Map();
    for (const w of d.terms) if (q.includes(w)) counts.set(w, (counts.get(w) || 0) + 1);
    let score = 0;
    const matched = [];
    for (const [w, n] of counts) {
      /* The naive scorer gives every hit the same weight. The weighted one divides by how many
         chunks contain the word, so a term in every chunk contributes almost nothing. */
      const weight = how === 'overlap' ? 1 : Math.log(1 + index.N / (1 + (index.df.get(w) || 0)));
      score += n * weight;
      matched.push(w);
    }
    return { ...d, score: Math.round(score * 100) / 100, matched };
  });
  const ranked = scored.filter((d) => d.score > 0).sort((a, b) => b.score - a.score);
  return { hits: ranked.slice(0, k), considered: index.N, anyFound: ranked.length > 0, how, query: q };
}

/**
 * Run both retrievers on the same question and report where they actually differ.
 *
 * MEASURED, NOT ASSERTED, and this matters. The obvious build is a strawman: cripple the naive
 * retriever until it returns nonsense, and the demonstration writes itself. On this corpus it does
 * not return nonsense — 63 chunks, every one about AI, so common words are genuinely informative
 * and the naive scorer is mostly right. Tested across eight questions it picked a different top
 * three on five of them, and the difference was the second and third hit, never the first.
 *
 * That is a truer lesson than a rigged one, and it is the same finding as the Code101 shelf: a
 * distinctiveness weighting earns its keep as a corpus grows and diversifies. On one course it is a
 * refinement; on a company's whole knowledge base it is the difference between the right document
 * and a confident wrong answer. The lab reports what these two did on YOUR question and says which
 * situation it is in.
 */
export function compare(index, question, k = 3) {
  const a = retrieve(index, question, { how: 'distinctive', k });
  const b = retrieve(index, question, { how: 'overlap', k });
  const ids = (r) => r.hits.map((h) => h.title);
  const same = JSON.stringify(ids(a)) === JSON.stringify(ids(b));
  const sharedTop = a.hits[0] && b.hits[0] && a.hits[0].title === b.hits[0].title;
  return { distinctive: a, overlap: b, same, sharedTop,
    differsFrom: same ? null : ids(a).findIndex((t, i) => t !== ids(b)[i]) + 1 };
}
export const WHY_SO_CLOSE = 'On this corpus the two retrievers usually agree, and that is not a flaw in the demonstration — it is what a small single-subject collection looks like. Sixty-three chunks, every one about AI, so even common words carry information. Weighting by rarity earns its keep as a corpus grows and diversifies: across a company\'s whole knowledge base it is the difference between the right document and a confident wrong answer.';

/** Step 2, literally: his template with the chunks and the question put in. */
export const stuff = (hits, question) =>
  `Here are some relevant excerpts:\n\n${hits.map((h, i) => `[${i + 1}] ${h.title}\n${h.body.slice(0, 400)}${h.body.length > 400 ? '…' : ''}`).join('\n\n')}\n\nNow, given those, answer this question: ${question}`;

/**
 * STEP 3 IS REFUSED, AND THE REFUSAL IS THE HONEST PART.
 *
 * There is no model here. A lab that printed a plausible answer would be doing precisely what §1
 * warns about — a fluent sentence with nothing behind it — inside the chapter that teaches
 * grounding. What it can say is what the answer WOULD be built from, which by his own argument is
 * the thing that determines it.
 */
export const WHY_NO_ANSWER = 'This lab has no model, so it will not show you an answer. It shows you exactly what a model would be handed — and by the module\'s own argument, that is what decides the answer. Bad chunks, confident wrong answer. Good chunks, useful right one. The model is the same either way.';
